import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useT } from '../i18n';
import { useLang } from '../context/LangContext';

// Shared minimal page wrapper matching Login.js style
function PageShell({ children }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--surface-base)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 16px',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 420,
        background: 'rgba(17,19,24,0.95)',
        border: '1px solid rgba(59,73,75,0.35)',
        padding: '40px 36px',
      }}>
        <div style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.2em',
          color: 'var(--brand-primary)',
          marginBottom: 32,
        }}>
          CRIT ROSTER
        </div>
        {children}
      </div>
    </div>
  );
}

export default function VerifyEmail({ token: propToken }) {
  const { lang } = useLang();
  const t = useT();

  // If a ?token= param is in the URL, we're in "click from email" mode
  const urlParams = new URLSearchParams(window.location.search);
  const urlToken  = urlParams.get('token');

  // "check inbox" mode — show after registration, with a resend button
  // "verifying"  — token in URL, calling API
  // "success"    — verified
  // "error"      — bad/expired token
  const [mode, setMode] = useState(urlToken ? 'verifying' : 'inbox');
  const [resending, setResending] = useState(false);
  const [resendDone, setResendDone] = useState(false);

  // If token is in URL, call verify endpoint immediately
  useEffect(() => {
    if (!urlToken) return;
    axios.get(`/api/auth/verify-email?token=${urlToken}`)
      .then(() => setMode('success'))
      .catch(() => setMode('error'));
  }, [urlToken]);

  const handleResend = async () => {
    const jwtToken = localStorage.getItem('token');
    if (!jwtToken) return;
    setResending(true);
    try {
      await axios.post('/api/auth/resend-verification', { lang }, {
        headers: { Authorization: `Bearer ${jwtToken}` },
      });
      setResendDone(true);
    } catch (_) {
      setResendDone(true); // show success anyway to avoid harvesting
    } finally {
      setResending(false);
    }
  };

  // ── Verifying (spinner) ──
  if (mode === 'verifying') {
    return (
      <PageShell>
        <div style={{ textAlign: 'center', color: '#849495', fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>
          {t('verify_checking')}
        </div>
      </PageShell>
    );
  }

  // ── Success ──
  if (mode === 'success') {
    return (
      <PageShell>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 20 }}>✓</div>
          <h2 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 800, color: '#e2e2e8' }}>
            {t('verify_success_title')}
          </h2>
          <p style={{ margin: '0 0 28px', fontSize: 13, color: 'rgba(185,202,203,0.7)', lineHeight: 1.6 }}>
            {t('verify_success_text')}
          </p>
          <a
            href="/login"
            style={{
              display: 'block',
              width: '100%',
              boxSizing: 'border-box',
              padding: '14px 20px',
              background: 'var(--brand-primary)',
              color: '#002022',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.14em',
              textDecoration: 'none',
              textAlign: 'center',
            }}
          >
            {t('verify_go_login')}
          </a>
        </div>
      </PageShell>
    );
  }

  // ── Error (expired / invalid token) ──
  if (mode === 'error') {
    return (
      <PageShell>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 20 }}>✕</div>
          <h2 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 800, color: '#e2e2e8' }}>
            {t('verify_error_title')}
          </h2>
          <p style={{ margin: '0 0 28px', fontSize: 13, color: 'rgba(185,202,203,0.7)', lineHeight: 1.6 }}>
            {t('verify_error_text')}
          </p>
          <a
            href="/"
            style={{
              display: 'block',
              width: '100%',
              boxSizing: 'border-box',
              padding: '14px 20px',
              background: 'transparent',
              border: '1px solid rgba(59,73,75,0.5)',
              color: '#849495',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.14em',
              textDecoration: 'none',
              textAlign: 'center',
            }}
          >
            {t('verify_go_home')}
          </a>
        </div>
      </PageShell>
    );
  }

  // ── Inbox mode (default after registration) ──
  return (
    <PageShell>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 20 }}>📬</div>
        <h2 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 800, color: '#e2e2e8' }}>
          {t('verify_inbox_title')}
        </h2>
        <p style={{ margin: '0 0 28px', fontSize: 13, color: 'rgba(185,202,203,0.7)', lineHeight: 1.6 }}>
          {t('verify_inbox_text')}
        </p>
        <a
          href="/"
          style={{
            display: 'block',
            width: '100%',
            boxSizing: 'border-box',
            padding: '14px 20px',
            background: 'var(--brand-primary)',
            color: '#002022',
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.14em',
            textDecoration: 'none',
            textAlign: 'center',
            marginBottom: 12,
          }}
        >
          {t('verify_go_app')}
        </a>
        {!resendDone ? (
          <button
            onClick={handleResend}
            disabled={resending}
            style={{
              background: 'transparent',
              border: 'none',
              color: resending ? '#849495' : 'rgba(var(--brand-primary-rgb),0.6)',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.1em',
              cursor: resending ? 'default' : 'pointer',
              padding: '8px 0',
            }}
          >
            {resending ? t('verify_resending') : t('verify_resend')}
          </button>
        ) : (
          <div style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10,
            color: 'var(--brand-secondary)',
            letterSpacing: '0.1em',
            padding: '8px 0',
          }}>
            {t('verify_resent')}
          </div>
        )}
      </div>
    </PageShell>
  );
}
